import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Rating } from 'react-simple-star-rating';
import { api_getReviews, api_insertCustomerReview, api_updateCustomerReview } from '~/backend/api_calls';
import UserSession from '~/backend/user_session';
import { notifyError, notifyInfo } from '~/components/common/ToastMessages';
import closeIcon from '~/images/close-button.svg';
import face5Stars from '~/images/face-5-stars.svg';
import faceDefault from '~/images/face-default.svg';
import faceHalfStars from '~/images/face-half-stars.svg';
import { toggleReviewsModal } from '~/store/reviews/actions';

const ReviewsModal = ({ product }) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const isOpen = useSelector(state => state.reviewsReducer.reviewsModalOpen);
	const [activeTab, setActiveTab] = useState('rating');
	const [rating, setRating] = useState(0);
	const [review, setReview] = useState('');
	const [ownReview, setOwnReview] = useState(null);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const getEmojiFace = value => {
		if (value >= 4) return <img src={face5Stars} alt="happy" />;
		if (value >= 2.5) return <img src={faceHalfStars} alt="smile" />;
		return <img src={faceDefault} alt="neutral" />;
	};

	const closeModal = () => {
		document.body.classList.remove('toggle-reviews-modal');
		dispatch(toggleReviewsModal(false));
	};

	useEffect(() => {
		const fetchReviews = async () => {
			if (!product?.id) return;
			try {
				const data = await api_getReviews(1, product.id);
				console.log(data);
				const userId = UserSession.getId();
				const userReview = data?.items?.find(x => x.from_id === parseInt(userId));
				if (userReview) {
					setRating(userReview.rating || 0);
					setReview(userReview.review || '');
					setOwnReview(userReview);
				}
			} catch (err) {
				console.error(err);
			}
		};
		if (isOpen) fetchReviews();
	}, [isOpen, product]);

	const handleSubmit = async e => {
		e.preventDefault();
		if (isSubmitting) return;

		if (rating < 0.5 || rating > 5) {
			notifyError(t('review_error_select_rating'));
			return;
		}
		if (review.trim().length > 1500) {
			notifyError(t('review_error_max_length'));
			return;
		}

		const payload = {
			to_ent: 1,
			to_id: product.id,
			rating,
			review
		};

		try {
			setIsSubmitting(true);
			if (ownReview?.id) {
				await api_updateCustomerReview({ ...payload, id: ownReview.id });
			} else {
				await api_insertCustomerReview(payload);
			}
			notifyInfo(t('review_success_message'));
			closeModal();
		} catch (err) {
			console.error(err);
			notifyError(t('review_error_general'));
		} finally {
			setIsSubmitting(false);
		}
	};

	if (!isOpen) return null;

	return (
		<div className={isOpen ? 'reviews-modal reviews-content' : 'reviews-modal'}>
			<main>
				<button type="button" className="close-button" onClick={closeModal}>
					<img src={closeIcon} alt="close-icon" />
				</button>
				<div className="reviews-modal-container">
					<ul className="tabs">
						<li className={activeTab === 'rating' ? 'active' : ''} onClick={() => setActiveTab('rating')}>
							1. {t('review_rating_tab')}
						</li>
						<li className={activeTab === 'comments' ? 'active' : ''} onClick={() => setActiveTab('comments')}>
							2. {t('review_comments_tab')}
						</li>
					</ul>

					{activeTab === 'rating' && (
						<div className="tab-container active">
							<div className="rating">
								<b>{t('review_question_rating')}</b>
								<div className="emoji">{getEmojiFace(rating)}</div>
								<Rating size={40} fillColor="#000" initialValue={rating} onClick={setRating} allowFraction />
							</div>
							<button className="next-button" type="button" onClick={() => setActiveTab('comments')}>
								{t('review_button_next')}
							</button>
						</div>
					)}

					{activeTab === 'comments' && (
						<form onSubmit={handleSubmit}>
							<div className="tab-container active">
								<div className="comments">
									<b>{t('review_question_comments')}</b>
									<textarea value={review} onChange={e => setReview(e.target.value)} />
								</div>
							</div>

							<div className="prev-next-button">
								<button className="previous-button" type="button" onClick={() => setActiveTab('rating')}>
									{'<'}
									{t('review_button_previous')}
								</button>
								<button className="submit-button" type="submit" disabled={isSubmitting}>
									{ownReview?.id ? t('review_button_update') : t('review_button_submit')}
								</button>
							</div>
						</form>
					)}
				</div>
			</main>
		</div>
	);
};

export default ReviewsModal;
